import { gql } from '@apollo/client';
import { ServiceName } from '@/api/types/ads.types';

export const AD_BASE_FIELDS = gql`
  fragment AdBaseFields on Ad {
    id
    title
    price
    updatedAt
    region
    path
    kinds
    legacyResourceId
    isBookmarked
    shop {
      id
    }
    photo {
      url
    }
    desktopPhoto: photo(source: DESKTOP) {
      url
    }
    status
  }
`;

export const GET_ADS = (adKind: ServiceName) => {
  const name = `GetAds_${adKind}`;
  return gql`
    ${AD_BASE_FIELDS}
    query ${name} (
      $adKind: AdKindEnum
      $orderType: AdOrderEnum
      $keywords: String
      $first: Int
      $after: String
      $source: SourceEnum!
      $filters: AdFilterInput
      $keywordsSource: KeywordSourceEnum
      $sourceLink: String
    ) {
      ads(
        adKind: $adKind
        first: $first
        after: $after
        source: $source
        orderType: $orderType
        keywords: $keywords
        filters: $filters
        keywordsSource: $keywordsSource
        sourceLink: $sourceLink
      ) {
        nodes {
          ...AdBaseFields
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  `;
};

export const CHECK_AD_EXISTENCE = gql`
  query CheckAdExistence($legacyId: ID!) {
    adExistence(legacyId: $legacyId)
  }
`;

// TODO: Remove when/if we move to ssr completely
// currently used to fetch some necessary data for performance reasons
export const GET_AD_DETAILS_COMPACT = gql`
  query GetAd($legacyId: ID!, $source: SourceEnum!) {
    adDetails(legacyId: $legacyId, source: $source) {
      id
      title
      body
      price
      region
      shop {
        id
        logo {
          url
        }
        name
        uri
        workingDays
        workingHours
        description
      }
      photos {
        url
      }
      photosPreview {
        url
      }
    }
  }
`;

export const GET_AD_DETAILS = gql`
  ${AD_BASE_FIELDS}
  query GetAd(
    $legacyId: ID!
    $source: SourceEnum!
    $transitionSourceBlock: TransitionSourceBlockEnum
  ) {
    adDetails(
      legacyId: $legacyId
      source: $source
      transitionSourceBlock: $transitionSourceBlock
    ) {
      id
      title
      body
      price
      updatedAt
      region
      azRegion: region(language: AZ)
      path
      kinds
      relatedAds {
        ...AdBaseFields
      }
      shop {
        id
        logo {
          url
        }
        name
        uri
        adsCount
        workingDays
        workingHours
        description
        isFavorited
        address
        longitude
        latitude
      }
      photos {
        url
      }
      photosPreview {
        url
      }
      bookmarksCount
      callsCount
      contact {
        id
        name
        logo {
          url
        }
        phones
        type
      }
      hits
      legacyResourceId
      path
      properties {
        name
        value
        link
      }
      azProperties {
        name
        value
      }
      status
      user {
        id
        legacyId
        publishedAdsCount
        isBlockedMe
        isBlockedByMe
      }
      deleteReasons {
        id
        name
        descriptionRequired
      }
      expiresAt
      chatPath
      vippedUntil
      featuredUntil
      bumpServices {
        price
        period
        previousPrice
      }
      vipServices {
        price
        period
        previousPrice
      }
      featuredServices {
        price
        period
        previousPrice
      }
      rejectReasons {
        id
        template
      }
      rejectReason
      isCallsAvailable
      isChatAvailable
      editLockedUntil
      isExpiredManually
      isPayFromBundleAvailable
      isProlongFree
      paymentMethods
      isBookmarked
      isAppleGooglePayEnabled
      hasEditPendingApproval
      isReportAvailable
      isAuthor
    }
  }
`;

export const GET_REPORT_REASONS = gql`
  query GetReportReasons($categoryId: ID!) {
    reportReasons(categoryId: $categoryId) {
      id
      name
      isDefault
    }
  }
`;

export const GET_AD_DETAILS_FOR_EDIT = gql`
  query GetAdDetailsForEdit($legacyId: ID!, $source: SourceEnum!) {
    adDetails(legacyId: $legacyId, source: $source) {
      id
      legacyResourceId
      title
      body
      price
      status
      editLockedUntil
      availableTemplatePhotos {
        id
        url
      }
      photos {
        id
        url
      }
      user {
        id
      }
      pendingEdit {
        body
        contactType
        photos {
          id
          url
        }
        price
        properties {
          legacyResourceId
          name
          type
          originalValue {
            ... on PropertyBooleanValue {
              value
            }
          }
        }
      }
      properties(onlyEditable: true) {
        legacyResourceId
        name
        type
        originalValue {
          ... on PropertyBooleanValue {
            value
          }
        }
      }
      category {
        isPriceRequired
        isPhotoRequiredOnUpdate
      }
      contact {
        type
      }
    }
  }
`;

export const GET_POPULAR_REQUESTS = gql`
  query PopularRequests($path: String) {
    popularRequests(path: $path) {
      term
      url
    }
  }
`;

export const GET_AD_HAS_EDIT_PENDING_APPROVAL = gql`
  query GetAdHasEditPendingApproval($legacyId: ID!) {
    ad(legacyId: $legacyId) {
      hasEditPendingApproval
    }
  }
`;
